import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Shield, CheckCircle, Star, TrendingUp, Clock, Award, MessageCircle, Users, Target, Zap, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { submitLeadWithGeoLocation } from '../services/centralLeadApi';
import { toast } from 'sonner';
import LPHeader from '../components/LPHeader';
import LPFooter from '../components/LPFooter';
import InquiryModal from '../components/InquiryModal';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { countryCodes } from '../utils/countryCodes';

// Why Choose Us Slider Component (Mobile Only)
const WhyChooseUsSlider = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'center' }, [
    Autoplay({ delay: 4000, stopOnInteraction: false })
  ]);

  const features = [
    { 
      icon: Shield, 
      title: 'USPTO-Registered Attorneys', 
      desc: 'Licensed IP attorneys with 15+ years experience review every trademark application for maximum USPTO approval success.',
      color: 'from-[#012169] to-[#012169]',
      stat: '15+ Years',
      statLabel: 'Experience'
    },
    { 
      icon: Zap, 
      title: '24-48 Hour Filing', 
      desc: 'Fast-track trademark registration service. We submit your USPTO application within 24-48 hours with expedited processing options.',
      color: 'from-[#F97316] to-[#F97316]',
      stat: '24-48 Hrs',
      statLabel: 'Filing Time'
    },
    { 
      icon: Target, 
      title: '98% USPTO Approval Rate', 
      desc: 'Industry-leading trademark approval success backed by comprehensive searches and expert application preparation. Money-back guarantee included.',
      color: 'from-[#012169] to-[#012169]',
      stat: '98%',
      statLabel: 'Approval Rate'
    }
  ];

  return (
    <div className="md:hidden relative px-12">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {features.map((item, index) => (
            <div key={index} className="flex-[0_0_100%] min-w-0">
              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 h-[340px] flex flex-col">
                {/* Icon with Gradient Background */}
                <div className="relative mb-5">
                  <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                    <item.icon className="h-8 w-8 text-white" />
                  </div>
                  {/* Stat Badge */}
                  <div className="absolute -top-2 -right-2 bg-white rounded-lg px-2 py-1 shadow-lg border-2 border-[#F97316]">
                    <span className="text-xs font-black text-[#012169]">{item.stat}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-base font-bold text-[#012169] mb-3">
                  {item.title}
                </h3>

                {/* Description with fixed height and scroll */}
                <div className="flex-1 overflow-y-auto mb-4">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>

                {/* Stat Label */}
                <div className="pt-3 border-t border-gray-100 mt-auto">
                  <span className="text-xs font-semibold text-gray-500">{item.statLabel}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={() => emblaApi?.scrollPrev()}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-100 shadow-lg rounded-full p-3 transition-colors duration-300"
        aria-label="Previous feature"
      >
        <ChevronLeft className="h-6 w-6 text-[#012169]" />
      </button>
      <button
        onClick={() => emblaApi?.scrollNext()}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-100 shadow-lg rounded-full p-3 transition-colors duration-300"
        aria-label="Next feature"
      >
        <ChevronRight className="h-6 w-6 text-[#012169]" />
      </button>
    </div>
  );
};

// Testimonials Slider Component
const TestimonialsSlider = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' }, [
    Autoplay({ delay: 3000, stopOnInteraction: false })
  ]);

  const testimonials = [
    { name: 'Sarah Mitchell', role: 'eCommerce Store Owner', service: 'Trademark', content: 'They made trademark registration so easy! Filed my application within 48 hours and kept me updated throughout. My brand is now federally protected.' },
    { name: 'Michael Rodriguez', role: 'Tech Startup Founder', service: 'Copyright', content: 'Professional copyright service for our software. The attorney team was knowledgeable and responsive. Highly recommend for any creative business.' },
    { name: 'Emily Chen', role: 'Fashion Designer', service: 'Trademark', content: 'Best decision I made for my fashion brand. The $45 offer was incredible value, and the service was top-notch. Now my designs are fully protected.' },
    { name: 'David Thompson', role: 'Restaurant Owner', service: 'Company Formation', content: 'Helped us form our LLC and register our restaurant trademark. Everything was handled professionally and quickly. Great team!' },
    { name: 'Jennifer Lopez', role: 'Fitness Coach', service: 'Patent', content: 'They guided us through patent application for our fitness equipment. Complex process made simple by their expert attorneys. Worth every penny.' }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl font-black text-[#012169] mb-6 tracking-tight">What Our Clients Say</h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">Real success stories from businesses we've helped protect</p>
        </div>

        {/* Slider for All Screens */}
        <div className="relative px-12">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="flex-[0_0_100%] md:flex-[0_0_calc(50%-12px)] lg:flex-[0_0_calc(33.333%-16px)] min-w-0">
                  <Card className="p-6 border-2 border-gray-200 hover:border-[#012169] hover:shadow-xl transition-all duration-300 flex flex-col h-[280px]">
                    <div className="flex space-x-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-[#F97316] text-[#F97316]" />
                      ))}
                    </div>
                    <div className="flex-1 mb-4">
                      <p className="text-gray-700 italic leading-relaxed text-sm">"{testimonial.content}"</p>
                    </div>
                    <div className="border-t pt-3 mt-auto">
                      <h4 className="font-semibold text-[#012169]">{testimonial.name}</h4>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                      <p className="text-xs text-[#F97316] font-medium mt-1">{testimonial.service} Service</p>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows - All Screens */}
          <button
            onClick={() => emblaApi?.scrollPrev()}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-100 shadow-lg rounded-full p-3 transition-colors duration-300"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="h-6 w-6 text-[#012169]" />
          </button>
          <button
            onClick={() => emblaApi?.scrollNext()}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-100 shadow-lg rounded-full p-3 transition-colors duration-300"
            aria-label="Next testimonial"
          >
            <ChevronRight className="h-6 w-6 text-[#012169]" />
          </button>
        </div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', brandName: '' });
  const [countryCode, setCountryCode] = useState('+1');
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => phone.replace(/[^\d]/g, '').length >= 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if all required fields are filled
    if (!formData.name || !formData.email || !formData.phone || !formData.brandName) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please check the agreement box to continue');
      return;
    }

    let hasErrors = false;
    const newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      hasErrors = true;
    }
    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsLoading(true);
    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        countryCode: countryCode,
        country: countryCodes.find(c => c.code === countryCode)?.name?.substring(0, 2)?.toUpperCase() || 'US',
        description: `Brand Name: ${formData.brandName || 'Not provided'}`
      };

      const result = await submitLeadWithGeoLocation(leadData, null, 'Landing Page Form');
      
      if (result.success) {
        toast.success(result.message || 'Inquiry submitted! We\'ll contact you soon.');
        setFormData({ name: '', email: '', phone: '', brandName: '' });
        // API will handle redirect automatically to /application/main?customerid=X
      } else {
        toast.error(result.message || 'Failed to submit. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again later.');
      setIsLoading(false);
    }
  };

  const handleOpenModal = (context) => {
    setModalContext(context);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <LPHeader onOpenModal={handleOpenModal} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 md:py-28 bg-gradient-to-br from-[#012169] via-[#012169] to-[#012169] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-96 h-96 bg-[#F97316] rounded-full blur-3xl "></div>
          <div className="absolute bottom-10 right-10 w-[500px] h-[500px] bg-[#F97316] rounded-full blur-3xl " style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-white/10 rounded-full blur-2xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Mobile/Tablet: Content appears second, Desktop: Content appears first */}
            <div className="order-2 lg:order-1">
              <div className="mb-6 hidden lg:block">
                <span className="inline-block bg-[#F97316] text-[#012169] font-extrabold text-sm px-6 py-2 rounded-full uppercase tracking-widest shadow-lg">
                  ⚡ Special Launch Price
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-6 lg:mb-8 leading-tight hidden lg:block tracking-tight">
                Secure Your Brand with <span className="text-[#F97316]">Federal Trademark Protection</span>
              </h1>
              <p className="text-base text-white/90 mb-8 lg:mb-10 leading-relaxed hidden lg:block font-light">
                Don't risk losing your brand identity. Our USPTO-registered attorneys provide complete trademark registration services—from comprehensive search to federal filing. Protect your business name, logo, and slogan with exclusive nationwide rights. Starting at just $45 with industry-leading 98% approval rate.
              </p>
              
              {/* CTA Button - Hidden on Mobile */}
              <div className="hidden lg:block">
                <Button
                  onClick={() => handleOpenModal('Register My Trademark - Hero CTA')}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white font-bold rounded-full shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  <span>Register My Trademark</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Button>
              </div>
            </div>

            {/* Mobile/Tablet: Form appears first, Desktop: Form appears second */}
            <div className="bg-white rounded-3xl shadow-2xl p-6 lg:p-10 order-1 lg:order-2 border-4 border-white/20 backdrop-blur-sm">
              {/* Form comes FIRST on mobile */}
              <div className="text-center mb-8 lg:hidden">
                <div className="inline-block bg-gradient-to-r from-[#012169] to-[#012169] text-white font-bold px-8 py-3 rounded-full mb-4 shadow-lg">
                  🎯 Get Started Today
                </div>
                <h3 className="text-base md:text-2xl font-black text-[#012169] mb-3 tracking-tight">Start Your Trademark Application</h3>
                <p className="text-base text-gray-600">Fill out the form below for a free consultation</p>
              </div>

              {/* Desktop Form Header */}
              <div className="text-center mb-8 hidden lg:block">
                <div className="inline-block bg-gradient-to-r from-[#012169] to-[#012169] text-white font-bold px-8 py-3 rounded-full mb-4 shadow-lg">
                  🎯 Get Started Today
                </div>
                <h3 className="text-base md:text-2xl font-black text-[#012169] mb-3 tracking-tight">Start Your Trademark Application</h3>
                <p className="text-base text-gray-600">Fill out the form below for a free consultation</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input type="text" placeholder="Full Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                
                <div>
                  <Input type="email" placeholder="Email Address *" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} onBlur={() => { if (formData.email && !validateEmail(formData.email)) { setErrors(prev => ({ ...prev, email: 'Invalid email' })); } else { setErrors(prev => ({ ...prev, email: '' })); }}} className={errors.email ? 'border-red-500' : ''} required />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <div className="flex gap-2">
                    <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="w-24 px-2 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium">
                      {countryCodes.map((country, index) => (<option key={index} value={country.code}>{country.flag} {country.code}</option>))}
                    </select>
                    <Input type="tel" placeholder="123-456-7890 *" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/[^\d\s\-]/g, '')})} onBlur={() => { if (formData.phone && !validatePhone(formData.phone)) { setErrors(prev => ({ ...prev, phone: 'Invalid phone' })); } else { setErrors(prev => ({ ...prev, phone: '' })); }}} className={`flex-1 ${errors.phone ? 'border-red-500' : ''}`} required />
                  </div>
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>

                <Input type="text" placeholder="Brand/Trademark Name *" value={formData.brandName} onChange={(e) => setFormData({...formData, brandName: e.target.value})} required />

                <div className="flex items-start space-x-2">
                  <input type="checkbox" id="agree-lp" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} className="mt-1 h-4 w-4 text-[#F97316] cursor-pointer" />
                  <label htmlFor="agree-lp" className="text-xs text-gray-600 cursor-pointer">I agree to receive communications from Crystal Trademark.</label>
                </div>

                <Button type="submit" disabled={isLoading} className="w-full bg-gradient-to-r from-[#F97316] to-[#F97316] hover:from-[#F97316] hover:to-[#F97316] text-[#012169] font-bold py-5 text-base rounded-xl shadow-2xl   disabled:opacity-50  ">
                  {isLoading ? '⏳ Submitting...' : '🚀 Submit Application Now'}
                </Button>
                <p className="text-center text-sm text-gray-600 mt-4 font-medium">✓ No Obligation • ✓ Secure & Confidential • ✓ Free Consultation</p>
              </form>

              {/* Mobile/Tablet Hero Content - Shows AFTER form on mobile */}
              <div className="lg:hidden mt-8 pt-8 border-t border-gray-200 text-center">
                <div className="mb-4">
                  <span className="inline-block bg-[#F97316] text-[#012169] font-extrabold text-xs px-4 py-2 rounded-full uppercase tracking-widest shadow-lg">
                    ⚡ Special Launch Price
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#012169] mb-3 leading-tight">
                  Lock Down Your Brand Before Competitors Steal It — Starting at <span className="text-[#F97316]">$45</span>
                </h2>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  Stop brand theft before it costs you thousands. Federal protection gives you exclusive legal rights nationwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why You Need This */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Grid */}
          <div className="hidden lg:grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image on left */}
            <div className="relative h-[320px] md:h-[420px] lg:h-[500px] rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1614036634955-ae5e90f9b9eb" 
                alt="Brand Identity Protection - Trademark Registration Services" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#012169]/50 to-transparent"></div>
            </div>

            {/* Content on right */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-[#012169] mb-8 leading-tight tracking-tight">Why USPTO Trademark Registration Protects Your Business</h2>
              <div className="space-y-6">
                <p className="text-base text-gray-700 leading-relaxed font-light">
                  Federal trademark registration through USPTO gives you exclusive nationwide rights to your brand name, logo, and slogan. Without it, competitors can legally copy your brand identity, confuse customers, and steal your market share—costing you thousands in lost revenue.
                </p>
                <p className="text-base text-gray-700 leading-relaxed font-light">
                  A registered trademark provides legal ammunition to stop infringers, qualify for Amazon Brand Registry, increase business valuation, and build investor confidence. USPTO registration is your legal shield against brand theft and the foundation for long-term business growth.
                </p>
                <div className="mt-8 p-6 bg-gradient-to-r from-[#F97316]/20 to-[#F97316]/5 border-l-4 border-[#F97316] rounded-xl shadow-lg">
                  <p className="text-base text-[#012169] font-bold leading-relaxed">
                    ⚠️ Warning: Every day without federal trademark protection puts your brand at risk. Competitors can file first and force you to rebrand. Secure your exclusive rights now—before it's too late.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Slider */}
          <div className="lg:hidden">
            <h2 className="text-2xl sm:text-3xl font-black text-[#012169] mb-8 text-center leading-tight tracking-tight">Why USPTO Trademark Registration Protects Your Business</h2>
            
            <div className="space-y-6">
              {/* Image First on Mobile */}
              <div className="relative h-[320px] md:h-[420px] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1614036634955-ae5e90f9b9eb" 
                  alt="Brand Identity Protection - Trademark Registration Services" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#012169]/50 to-transparent"></div>
              </div>

              {/* Content Below Image on Mobile */}
              <div className="space-y-6">
                <p className="text-base text-gray-700 leading-relaxed font-light">
                  Federal trademark registration through USPTO gives you exclusive nationwide rights to your brand name, logo, and slogan. Without it, competitors can legally copy your brand identity, confuse customers, and steal your market share—costing you thousands in lost revenue.
                </p>
                <p className="text-base text-gray-700 leading-relaxed font-light">
                  A registered trademark provides legal ammunition to stop infringers, qualify for Amazon Brand Registry, increase business valuation, and build investor confidence. USPTO registration is your legal shield against brand theft and the foundation for long-term business growth.
                </p>
                <div className="mt-8 p-6 bg-gradient-to-r from-[#F97316]/20 to-[#F97316]/5 border-l-4 border-[#F97316] rounded-xl shadow-lg">
                  <p className="text-base text-[#012169] font-bold leading-relaxed">
                    ⚠️ Warning: Every day without federal trademark protection puts your brand at risk. Competitors can file first and force you to rebrand. Secure your exclusive rights now—before it's too late.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Redesigned */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#F97316]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#012169]/10 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#F97316] to-[#F97316] rounded-2xl shadow-lg mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-[#012169] mb-4 tracking-tight">
              How Trademark Registration Works
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Simple USPTO filing process from search to federal registration—all handled by licensed attorneys
            </p>
          </div>

          {/* Desktop: Timeline Design */}
          <div className="hidden md:block relative">
            {/* Horizontal connecting line (desktop) */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#F97316] via-[#012169] to-[#012169]  -translate-y-1/2"></div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {[
                { 
                  step: '1', 
                  title: 'Free Trademark Consultation', 
                  desc: 'Submit your brand details for a complimentary trademark evaluation. Our specialists assess registrability and identify conflicts within 24 hours.',
                  icon: (
                    <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )
                },
                { 
                  step: '2', 
                  title: 'Comprehensive USPTO Search', 
                  desc: 'Licensed attorneys conduct thorough searches across USPTO federal database, state records, and common law sources to ensure trademark availability.',
                  icon: (
                    <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )
                },
                { 
                  step: '3', 
                  title: 'USPTO Filing & Registration', 
                  desc: 'We prepare your trademark application, file with USPTO, and provide ongoing monitoring until you receive your federal registration certificate.',
                  icon: (
                    <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )
                }
              ].map((item, index) => (
                <div key={index} className="relative group">
                  {/* Step Card */}
                  <div className="relative">
                    {/* Large Step Number Circle */}
                    <div className="absolute -left-4 md:left-1/2 md:-translate-x-1/2 top-0 md:-top-16 z-20 w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-[#F97316] to-[#F97316] flex items-center justify-center shadow-2xl border-4 border-white">
                      <span className="text-2xl md:text-3xl font-black text-white">{item.step}</span>
                    </div>

                    {/* Card Content */}
                    <div className="ml-16 md:ml-0 md:mt-8 bg-white rounded-2xl p-6 md:p-8 shadow-lg border-2 border-gray-100">
                      {/* Icon */}
                      <div className="w-16 h-16 mx-auto mb-6 text-[#012169]">
                        {item.icon}
                      </div>

                      {/* Title */}
                      <h3 className="text-base md:text-2xl font-bold text-[#012169] mb-3 text-center">
                        {item.title}
                      </h3>

                      {/* Description */}
                      <p className="text-base text-gray-600 text-center leading-relaxed">
                        {item.desc}
                      </p>

                      {/* Step Indicator Bar */}
                      <div className="mt-6 pt-6 border-t border-gray-100">
                        <div className="flex items-center justify-center gap-2">
                          <div className={`h-1 rounded-full ${
                            index === 0 ? 'w-12 bg-[#F97316]' : 'w-6 bg-gray-300'
                          }`}></div>
                          <div className={`h-1 rounded-full ${
                            index === 1 ? 'w-12 bg-[#F97316]' : 'w-6 bg-gray-300'
                          }`}></div>
                          <div className={`h-1 rounded-full ${
                            index === 2 ? 'w-12 bg-[#012169]' : 'w-6 bg-gray-300'
                          }`}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile: Slider */}
          <div className="md:hidden overflow-hidden">
            <div className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hide px-4">
              {[
                { 
                  step: '1', 
                  title: 'Free Trademark Consultation', 
                  desc: 'Submit your brand details for a complimentary trademark evaluation. Our specialists assess registrability and identify conflicts within 24 hours.',
                  icon: (
                    <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )
                },
                { 
                  step: '2', 
                  title: 'Comprehensive USPTO Search', 
                  desc: 'Licensed attorneys conduct thorough searches across USPTO federal database, state records, and common law sources to ensure trademark availability.',
                  icon: (
                    <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )
                },
                { 
                  step: '3', 
                  title: 'USPTO Filing & Registration', 
                  desc: 'We prepare your trademark application, file with USPTO, and provide ongoing monitoring until you receive your federal registration certificate.',
                  icon: (
                    <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )
                }
              ].map((item, index) => (
                <div key={index} className="flex-shrink-0 w-80 snap-center">
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 h-full flex flex-col">
                    {/* Step Number Circle */}
                    <div className="flex justify-center mb-6">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#F97316] to-[#F97316] flex items-center justify-center shadow-lg border-4 border-white">
                        <span className="text-2xl font-black text-white">{item.step}</span>
                      </div>
                    </div>

                    {/* Icon */}
                    <div className="w-16 h-16 mx-auto mb-6 text-[#012169]">
                      {item.icon}
                    </div>

                    {/* Title */}
                    <h3 className="text-base font-bold text-[#012169] mb-3 text-center">
                      {item.title}
                    </h3>

                    {/* Description */}
                    <p className="text-base text-gray-600 text-center leading-relaxed flex-grow">
                      {item.desc}
                    </p>

                    {/* Step Indicator Bar */}
                    <div className="mt-6 pt-6 border-t border-gray-100">
                      <div className="flex items-center justify-center gap-2">
                        <div className={`h-1 rounded-full  ${
                          index === 0 ? 'w-12 bg-[#F97316]' : 'w-6 bg-gray-300'
                        }`}></div>
                        <div className={`h-1 rounded-full  ${
                          index === 1 ? 'w-12 bg-[#F97316]' : 'w-6 bg-gray-300'
                        }`}></div>
                        <div className={`h-1 rounded-full  ${
                          index === 2 ? 'w-12 bg-[#012169]' : 'w-6 bg-gray-300'
                        }`}></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">← Swipe to see all steps →</p>
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <p className="text-base text-gray-600 mb-6">
              Ready to protect your brand?
            </p>
            <Button
              size="lg"
              onClick={() => handleOpenModal('How It Works - Get Started')}
              className="bg-gradient-to-r from-[#F97316] to-[#F97316]  text-[#012169]  font-black px-12 py-7 text-base rounded-xl shadow-2xl   "
            >
              Start Your Application Now
            </Button>
          </div>
        </div>
      </section>

          {/* Mobile design is now handled in the unified timeline design above */}

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-base font-bold text-[#F97316] uppercase tracking-widest mb-4">
              OUR PRICING
            </p>
            <h2 className="text-2xl sm:text-3xl font-black text-[#012169] mb-6 tracking-tight">
              Transparent Trademark Registration Pricing
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Professional USPTO trademark filing from $45. All packages include licensed IP attorney review, comprehensive trademark search, and federal registration filing. Choose the protection level that fits your brand needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                id: 'basic', 
                name: 'Basic', 
                price: 45, 
                popular: false,
                features: ['Case Review', 'Case Filing', 'Basic Search', 'State E-Filing', 'Trademark Secured', 'Direct Hit Search', 'Dedicated Case Manager']
              },
              { 
                id: 'deluxe', 
                name: 'Deluxe', 
                price: 145, 
                popular: false,
                features: ['Case Review', 'Case Filing', 'Basic Search', 'State E-Filing', 'Trademark Secured', 'Direct Hit Search', 'Dedicated Case Manager']
              },
              { 
                id: 'premium', 
                name: 'Premium', 
                price: 299, 
                popular: true,
                features: ['Case Review (by TM Expert)', 'Basic Free Trademark Search', 'Trademark Monitoring (3 Months)', 'Trademark Secured', 'Refusal Risk Meter', 'Complete Paperwork']
              },
              { 
                id: 'elite', 
                name: 'Elite', 
                price: 599, 
                popular: false,
                features: ['Case Review & Filing (IP Attorney)', 'Assigned Case Officer', 'Private Registration', 'Trademark Secured', 'Federal Search & Clearance', 'Federal E-Filing (USPTO)', 'Cease & Desist Letter', 'Same Day Filing', 'Trademark Monitoring (1 Year)', 'Direct Hit Search', 'Case Manager', '24/7 Access', '100% Approval Guarantee']
              }
            ].map((plan, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden bg-white transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 ${
                  plan.popular 
                    ? 'border-4 border-[#012169] shadow-2xl scale-105' 
                    : 'border-2 border-gray-200 hover:border-[#012169]'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-[#012169] to-[#F97316] text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-base md:text-2xl font-bold text-[#012169] mb-4">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-[#F97316]">${plan.price}</span>
                    <span className="text-gray-600 text-sm block mt-1">+USPTO fees</span>
                  </div>
                  <ul className="space-y-3 mb-6 h-[320px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-[#F97316] scrollbar-track-gray-200">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-[#F97316] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => handleOpenModal(`Get Started - ${plan.name} Plan`)} 
                    className={`w-full font-semibold ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-[#012169] to-[#F97316] text-white' 
                        : 'bg-[#012169] text-white'
                    }`}
                  >
                    Get Started
                  </Button>
                  <p className="text-xs text-gray-500 text-center mt-3">*(Govt. filing fee not included)</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Redesigned */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#F97316]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-0 w-[500px] h-[500px] bg-[#012169]/10 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#012169] to-[#F97316] rounded-2xl shadow-lg mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-[#012169] mb-4 tracking-tight">
              Why 10,000+ Businesses Trust Our Trademark Service
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Industry-leading trademark registration with 98% USPTO approval rate and licensed IP attorneys
            </p>
          </div>

          {/* Mobile: Slider */}
          <WhyChooseUsSlider />

          {/* Desktop: Grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                icon: Shield, 
                title: 'USPTO-Registered Attorneys', 
                desc: 'Licensed IP attorneys with 15+ years experience handle every trademark application with precision. Each filing reviewed by legal experts to maximize approval chances.',
                color: 'from-[#012169] to-[#012169]',
                stat: '15+ Years',
                statLabel: 'Experience'
              },
              { 
                icon: Zap, 
                title: '24-48 Hour USPTO Filing', 
                desc: 'Fast-track trademark protection. We submit your federal application to USPTO within 24-48 hours with optional expedited processing for urgent needs.',
                color: 'from-[#F97316] to-[#F97316]',
                stat: '24-48 Hrs',
                statLabel: 'Filing Time'
              },
              { 
                icon: Target, 
                title: '98% USPTO Approval Success', 
                desc: 'Industry-leading approval rate backed by comprehensive trademark searches and expert application preparation. Money-back guarantee on all packages.',
                color: 'from-[#012169] to-[#012169]',
                stat: '98%',
                statLabel: 'Approval Rate'
              }
            ].map((item, index) => (
              <div key={index} className="group">
                <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-100 h-full flex flex-col hover:border-[#012169] hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  {/* Icon with Gradient Background */}
                  <div className="relative mb-6">
                    <div className={`w-20 h-20 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                      <item.icon className="h-10 w-10 text-white" />
                    </div>
                    {/* Stat Badge */}
                    <div className="absolute -top-2 -right-2 bg-white rounded-lg px-3 py-1 shadow-lg border-2 border-[#F97316]">
                      <span className="text-xs font-black text-[#012169]">{item.stat}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-base md:text-2xl font-bold text-[#012169] mb-3">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-base text-gray-600 leading-relaxed mb-6 flex-grow">
                    {item.desc}
                  </p>

                  {/* Stat Label */}
                  <div className="pt-4 border-t border-gray-100">
                    <span className="text-sm font-semibold text-gray-500">{item.statLabel}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { number: '10,000+', label: 'Brands Protected' },
              { number: '98%', label: 'Success Rate' },
              { number: '24-48 Hrs', label: 'Filing Time' },
              { number: '15+ Years', label: 'Experience' }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-xl shadow-md border-2 border-gray-100 hover:border-[#012169] hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-[#012169] to-[#F97316] bg-clip-text text-transparent mb-2">
                  {item.number}
                </div>
                <div className="text-sm font-semibold text-gray-600">
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 md:py-20 lg:py-28 bg-gradient-to-r from-[#012169] via-[#012169] to-[#012169] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1759266585745-69800edcd54d" 
            alt="Abstract vibrant waves"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#012169]/50 to-transparent"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 lg:mb-8 leading-tight tracking-tight">Claim Your Brand Before Someone Else Does</h2>
          <p className="text-base text-white/95 mb-8 lg:mb-12 leading-relaxed max-w-3xl mx-auto font-light">
            Your brand is your business identity—and it's vulnerable without federal trademark protection. Right now, competitors could be filing for your brand name. Over 12,000+ businesses trust us to secure their trademarks fast. Lock in our limited $45 filing offer and protect what you've built.
          </p>
          <Button onClick={() => handleOpenModal('CTA Section - Protect Brand')} className="bg-gradient-to-r from-[#F97316] to-[#F97316] text-white font-black px-6 sm:px-10 md:px-12 lg:px-16 py-4 sm:py-5 md:py-6 lg:py-7 text-sm sm:text-base md:text-base lg:text-2xl rounded-2xl shadow-2xl leading-tight">
            <span className="inline-block">🚀 Secure My Trademark for $45 Today</span>
          </Button>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSlider />

      {/* FAQs - Modern Design */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#F97316]/10 to-[#F97316]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#012169]/10 to-[#F97316]/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#F97316] to-[#F97316] rounded-2xl shadow-lg mb-4 mx-auto">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-[#012169] mb-4 tracking-tight">
              Questions? We've Got Answers
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Everything you need to know about protecting your brand
            </p>
          </div>

          <div className="space-y-4">
            {[
              { q: 'How long does trademark registration take?', a: 'The entire trademark registration process typically takes 8-12 months from filing to final approval. However, we file your application within 24-48 hours of receiving your information. The majority of time is spent in the USPTO review queue, which we actively monitor and manage for you.' },
              { q: 'What\'s included in the $45 trademark registration offer?', a: 'Our $45 offer includes a basic trademark search, filing assistance, document review, and submission to the USPTO. This is perfect for straightforward trademark applications. For comprehensive attorney-prepared applications with full legal review, we recommend our Standard or Professional packages.' },
              { q: 'Can I trademark a name that\'s already being used?', a: 'It depends. If someone else has already registered the same or similar trademark in your industry, you likely cannot use it. That\'s why we conduct a comprehensive trademark search before filing. If a conflict exists, we\'ll notify you and suggest alternatives to avoid rejection and wasted filing fees.' },
              { q: 'Do I really need a trademark, or can I just use my business name?', a: 'While you can use any business name without registration, only a registered trademark gives you exclusive nationwide rights and legal protection. Without registration, competitors can use similar names, causing customer confusion and damaging your brand. A trademark also increases business value and credibility.' },
              { q: 'What if my trademark application gets rejected?', a: 'We maintain a 98% approval rate, but if your application faces rejection, our attorneys will respond to office actions and work to overcome objections. Our Standard and higher packages include free office action responses. If approval is ultimately impossible, we offer a money-back guarantee on select packages.' }
            ].map((faq, index) => (
              <div
                key={index}
                className={`group bg-white rounded-2xl  ${
                  openFAQ === index
                    ? 'shadow-2xl shadow-[#F97316]/20 border-2 border-[#F97316]'
                    : 'shadow-lg  border-2 border-gray-100 hover:border-[#F97316]/50'
                }`}
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full p-6 text-left flex items-center justify-between "
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center  ${
                      openFAQ === index
                        ? 'bg-gradient-to-br from-[#F97316] to-[#F97316] shadow-lg'
                        : 'bg-gradient-to-br from-gray-100 to-gray-50 group-hover:from-[#F97316] group-hover:to-[#F97316]'
                    }`}>
                      <span className={`font-black text-base ${
                        openFAQ === index
                          ? 'text-white'
                          : 'text-gray-600 group-'
                      }`}>
                        Q{index + 1}
                      </span>
                    </div>
                    <h3 className={`text-base md:text-base font-bold pr-4  ${
                      openFAQ === index
                        ? 'text-[#012169]'
                        : 'text-gray-800 group-hover:text-[#012169]'
                    }`}>
                      {faq.q}
                    </h3>
                  </div>
                  <div className={`flex-shrink-0  ${
                    openFAQ === index ? 'rotate-180' : ''
                  }`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      openFAQ === index
                        ? 'bg-[#012169]'
                        : 'bg-gray-100 group-hover:bg-[#012169]'
                    }`}>
                      <ChevronDown className={`h-5 w-5 ${
                        openFAQ === index
                          ? 'text-white'
                          : 'text-gray-600 group-'
                      }`} />
                    </div>
                  </div>
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-6 animate-in slide-in-from-top-2 duration-300">
                    <div className="pl-16 pr-4">
                      <div className="p-5 bg-gradient-to-r from-[#F97316]/10 to-transparent rounded-xl border-l-4 border-[#F97316]">
                        <p className="text-base text-gray-700 leading-relaxed">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <LPFooter />
      <InquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonContext={modalContext} />
    </div>
  );
};

export default LandingPage;